import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import { I18nProvider } from "@/context/I18nContext";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import useVisitTracking from "@/hooks/useVisitTracking";
import usePosthogSpaPageviews from "@/hooks/usePosthogSpaPageviews";

// Public
import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Assessment from "@/pages/Assessment";
import Consultation from "@/pages/Consultation";
import Campaigns from "@/pages/Campaigns";
import Packages from "@/pages/Packages";
import Articles from "@/pages/Articles";
import News from "@/pages/News";
import QuoteRequest from "@/pages/QuoteRequest";
import Trainings from "@/pages/Trainings";
import Contact from "@/pages/Contact";
import { PolicyList, PolicyDetail } from "@/pages/Policies";
import DataRights from "@/pages/DataRights";
import LogoPreview from "@/pages/LogoPreview";

// Admin
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminSections from "@/pages/admin/AdminSections";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminArticles from "@/pages/admin/AdminArticles";
import AdminNews from "@/pages/admin/AdminNews";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminPolicies from "@/pages/admin/AdminPolicies";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminBackups from "@/pages/admin/AdminBackups";
import AdminGovernance from "@/pages/admin/AdminGovernance";
import AdminAssessment from "@/pages/admin/AdminAssessment";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminGdpr from "@/pages/admin/AdminGdpr";
import AdminServices from "@/pages/admin/AdminServices";
import AdminTrainings from "@/pages/admin/AdminTrainings";

// Public auth utility pages
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

/** Router-scoped side effects: visitor counter + PostHog SPA pageviews. */
function RouterEffects() {
  useVisitTracking();
  usePosthogSpaPageviews();
  return null;
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouterEffects />
            <Toaster position="top-center" richColors />
            <Routes>
              {/* Public routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
              <Route path="/training" element={<Trainings />} />
                <Route path="/assessment" element={<Assessment />} />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/news" element={<News />} />
                <Route path="/quote" element={<QuoteRequest />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/policies" element={<PolicyList />} />
                <Route path="/policies/:key" element={<PolicyDetail />} />
                <Route path="/data-rights" element={<DataRights />} />
              </Route>

              {/* Standalone brand preview */}
              <Route path="/logo-preview" element={<LogoPreview />} />

              {/* Password recovery */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminOverview />} />
                <Route path="profile" element={<AdminProfile />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="team" element={<AdminTeam />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="gdpr" element={<AdminGdpr />} />
                <Route path="governance" element={<AdminGovernance />} />
                <Route path="assessment" element={<AdminAssessment />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
                <Route path="sections" element={<AdminSections />} />
                <Route path="inbox" element={<AdminInbox />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="trainings" element={<AdminTrainings />} />
                <Route path="articles" element={<AdminArticles />} />
                <Route path="news" element={<AdminNews />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="packages" element={<AdminPackages />} />
                <Route path="policies" element={<AdminPolicies />} />
                <Route path="backups" element={<AdminBackups />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
